import { UserPosition } from '@/domain/market-info/marketInfo'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { Address } from 'viem'
import { calculatePooledSpendPowerUSD } from './pooledSpendPower'
import { useSolverOptions } from './useSolverOptions'

interface UseHypurrFiCardDataParams {
  chainId: number
  borrowTokenAddress?: Address
  enabled: boolean
}

export function useHypurrFiCardData({ chainId, borrowTokenAddress, enabled }: UseHypurrFiCardDataParams) {
  const { marketInfo } = useMarketInfo({ chainId })
  const { hypurrFiWithdrawTokens } = useSolverOptions()

  // Get user positions with collateral or borrows
  const userPositions = useMemo(
    () =>
      marketInfo.userPositions.filter((position) => position.collateralBalance.gt(0) || position.borrowBalance.gt(0)),
    [marketInfo.userPositions],
  )

  // Calculate total collateral in USD
  const totalCollateralUSD = useMemo(() => {
    if (!enabled) return 0
    return userPositions
      .reduce((sum, position) => {
        const collateralUSD = position.collateralBalance.multipliedBy(position.reserve.priceInUSD)
        return sum.plus(collateralUSD)
      }, new BigNumber(0))
      .toNumber()
  }, [enabled, userPositions])

  // Calculate total borrowed in USD
  const totalBorrowUSD = useMemo(() => {
    if (!enabled) return new BigNumber(0)
    return userPositions.reduce((sum, position) => {
      const borrowUSD = position.borrowBalance.multipliedBy(position.reserve.priceInUSD)
      return sum.plus(borrowUSD)
    }, new BigNumber(0))
  }, [enabled, userPositions])

  const remainingBorrowCapacityUSD = useMemo(() => {
    if (!enabled) return new BigNumber(0)
    return new BigNumber(marketInfo.userPositionSummary.availableBorrowsUSD.toString())
  }, [enabled, marketInfo.userPositionSummary.availableBorrowsUSD])

  // Get borrow token info
  const borrowToken = useMemo(() => {
    if (!borrowTokenAddress) return undefined
    return marketInfo.reserves.find(
      (reserve) => reserve.token.address.toLowerCase() === borrowTokenAddress.toLowerCase(),
    )?.token
  }, [marketInfo.reserves, borrowTokenAddress])

  // Calculate borrow APY
  const borrowAPY = useMemo(() => {
    if (!enabled || !borrowTokenAddress) return 0

    const borrowPosition = userPositions.find(
      (position) => position.reserve.token.address.toLowerCase() === borrowTokenAddress.toLowerCase(),
    )

    return (
      borrowPosition?.reserve.variableBorrowApy?.multipliedBy(100).toNumber() ||
      marketInfo.reserves
        .find((reserve) => reserve.token.address.toLowerCase() === borrowTokenAddress.toLowerCase())
        ?.variableBorrowApy?.multipliedBy(100)
        .toNumber() ||
      0
    )
  }, [enabled, borrowTokenAddress, userPositions, marketInfo.reserves])

  // Get health factor
  const healthFactor = useMemo(() => {
    if (!enabled) return 'N/A'
    const hf = marketInfo.userPositionSummary.healthFactor
    if (!hf) return 'N/A'
    const hfNumber = hf.toNumber()
    return hfNumber > 100 ? '>100' : hfNumber.toFixed(2)
  }, [enabled, marketInfo.userPositionSummary.healthFactor])

  // Filter positions with collateral
  const collateralPositions = useMemo(() => {
    if (!enabled) return []
    return userPositions.filter((position) => position.collateralBalance.gt(0))
  }, [enabled, userPositions])

  const spendModeCollateralPositions = useMemo(() => {
    if (!enabled) return []

    const pooledPositions = userPositions.filter((position) => position.collateralBalance.gt(0))
    // Only show tokens the backend supports for withdraw/spend, not all borrow tokens
    const supportedTokens = hypurrFiWithdrawTokens.map((token) => token.address.toLowerCase())

    const selectedPositions: (UserPosition & { spendingPowerUSD: BigNumber })[] = []
    for (const tokenAddress of supportedTokens) {
      const existingPosition = pooledPositions.find(
        (position) => position.reserve.token.address.toLowerCase() === tokenAddress,
      )

      if (existingPosition) {
        const collateralAmountUSD = existingPosition.collateralBalance.multipliedBy(existingPosition.reserve.priceInUSD)

        selectedPositions.push({
          ...existingPosition,
          spendingPowerUSD: calculatePooledSpendPowerUSD({
            collateralAmountUSD,
            maxLtv: existingPosition.reserve.maxLtv,
            remainingBorrowCapacityUSD,
            hasDebt: totalBorrowUSD.gt(0),
            usageAsCollateralEnabledOnUser: existingPosition.reserve.usageAsCollateralEnabledOnUser,
            eModeCategory: existingPosition.reserve.eModeCategory,
            eModeState: marketInfo.userConfiguration.eModeState,
          }),
        })
        continue
      }

      const reserve = marketInfo.reserves.find((item) => item.token.address.toLowerCase() === tokenAddress)
      if (!reserve) continue

      selectedPositions.push({
        reserve,
        scaledVariableDebt: new BigNumber(0),
        scaledATokenBalance: new BigNumber(0),
        collateralBalance: NormalizedUnitNumber(0),
        borrowBalance: NormalizedUnitNumber(0),
        spendingPowerUSD: new BigNumber(0),
      })
    }

    return selectedPositions
  }, [
    enabled,
    userPositions,
    marketInfo.reserves,
    marketInfo.userConfiguration.eModeState,
    hypurrFiWithdrawTokens,
    remainingBorrowCapacityUSD,
    totalBorrowUSD,
  ])

  // Calculate max borrow capacity
  const maxBorrowCapacityUSD = useMemo(() => {
    if (!enabled) return 0
    return marketInfo.userPositionSummary.availableBorrowsUSD.plus(totalBorrowUSD).toNumber()
  }, [enabled, marketInfo.userPositionSummary.availableBorrowsUSD, totalBorrowUSD])

  return {
    userPositions,
    collateralPositions,
    spendModeCollateralPositions,
    totalCollateralUSD,
    totalBorrowUSD,
    borrowToken,
    borrowTokenSymbol: borrowToken?.symbol,
    borrowAPY,
    healthFactor,
    maxBorrowCapacityUSD,
    marketInfo,
  }
}
